import React, { use, useMemo, useState } from 'react';

import {
	Box,
	Button,
	Container,
	Grid,
	Paper,
	Tab,
	Tabs,
	Typography
} from '@mui/material';
import { Subheader, TextInput, TabPanel } from 'components';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAcademyEventSubmission from 'hooks/useAcademyEventSubmission';
import { capitalize } from 'lodash';
import { useAlerts } from 'hooks';
import api from 'api';
import FootBallCV from './FootballCV';
import UserPlayer from 'features/events/user/UserPlayer/UserPlayer';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';

const PLAYER_CARD = [
	'control',
	'defending',
	'dribbling',
	'passing',
	'physical',
	'shooting'
];

const SubmissionShow = () => {
	const { id } = useParams();
	const searchParams = new URLSearchParams(window.location.search);
	const academy_id = searchParams.get('academy_id');
	const status = searchParams.get('status') ?? 'submitted';
	const navigate = useNavigate();
	const [innerTabValue, setInnerTabValue] = useState(0);

	const [feedbackForm, setFeedbackForm] = useState({
		comments: {},
		skills: {}
	});

	const {
		eventSubmissionResource,
		isLoading,
		isEmpty,
		isLoaded,
		findManyEventSubmissions,
		eventSubmissionResources,
		page,
		numPages
	} = useAcademyEventSubmission({
		url: `/api/v1/admin/academies/${academy_id}/academy_event_submissions`
	});
	const { showAlertError, showAlertWarning, showAlertSuccess } = useAlerts();

	useEffect(() => {
		findManyEventSubmissions({
			status: status,
			page: page,
			view_id: id,
			per_page: 1
		});
	}, [id]);

	const currentEventSubmission = useMemo(() => {
		if (eventSubmissionResources && eventSubmissionResources.length > 0) {
			return eventSubmissionResources[0];
		}

		return null;
	}, [eventSubmissionResources]);

	useEffect(() => {
		if (isLoaded && currentEventSubmission) {
			currentEventSubmission?.submissions?.forEach((submission) => {
				setFeedbackForm((prev) => ({
					...prev,
					comments: {
						...prev.comments,
						[submission.id]: {
							comment: submission.feedback ? submission.feedback.comment : ''
						}
					}
				}));

				PLAYER_CARD.forEach((key) => {
					setFeedbackForm((prev) => ({
						...prev,
						skills: {
							...prev.skills,
							[key]: currentEventSubmission.user?.player_card
								? currentEventSubmission.user.player_card[key]
								: ''
						}
					}));
				});
			});
		}
	}, [eventSubmissionResource, isLoaded, currentEventSubmission]);

	const handleFeedbackChange = (e) => {
		const { name, value } = e.target;
		setFeedbackForm({
			...feedbackForm,
			comments: {
				...feedbackForm.comments,
				[name]: {
					comment: value
				}
			}
		});
	};

	const handleSkillChange = (e) => {
		const { name, value } = e.target;

		if (value < 0 || value > 99) {
			showAlertWarning('Skill rating must be between 0 and 99');
			return;
		}

		setFeedbackForm({
			...feedbackForm,
			skills: {
				...feedbackForm.skills,
				[name]: value
			}
		});
	};

	const handleSubmitFeedback = async () => {
		try {
			const commentsPayload = Object.keys(feedbackForm.comments).map((key) => ({
				id: parseInt(key),
				comment: feedbackForm.comments[key].comment
			}));

			const response = await api.post(
				`/api/v1/admin/academies/${currentEventSubmission.event.academy_id}/academy_event_submissions/${currentEventSubmission.id}/feedback`,
				{
					feedback: {
						comments: commentsPayload,
						skills: feedbackForm.skills
					},
					user_id: currentEventSubmission.user.id,
					event_id: currentEventSubmission.event.id,
					event_submission_id: currentEventSubmission.id
				}
			);

			if (response.data) {
				showAlertSuccess('Feedback submitted successfully');
				navigate(-1);
			}
		} catch (error) {
			showAlertError('Error submitting feedback');
			console.log(error);
		}
	};

	const handleTabChange = (_, newValue) => {
		setInnerTabValue(newValue);
	};

	const handlePrevious = () =>
		findManyEventSubmissions(
			{
				status: status,
				per_page: 1
			},
			page - 1
		);

	const handleNext = () =>
		findManyEventSubmissions(
			{
				status: status,
				per_page: 1
			},
			page + 1
		);

	return (
		<div>
			<Subheader
				title={
					isLoading
						? 'Loading...'
						: currentEventSubmission?.event?.name || 'Submission Detail'
				}
				buttons={[
					<Button
						key="previous"
						onClick={handlePrevious}
						color="primary"
						variant="outlined"
						startIcon={<ArrowBackIos />}
						disabled={page <= 1}
						size="medium"
					>
						Previous Player{' '}
					</Button>,
					<Button
						key="next"
						onClick={handleNext}
						color="primary"
						variant="outlined"
						size="medium"
						disabled={page >= numPages}
						endIcon={<ArrowForwardIos />}
					>
						Next Player
					</Button>
				]}
			/>

			{currentEventSubmission && !isEmpty && feedbackForm && (
				<Container>
					<Tabs
						value={innerTabValue}
						onChange={handleTabChange}
						aria-label="Submissions Tabs"
					>
						<Tab label="FeedBack" />
						<Tab label="Player Bio" />
					</Tabs>

					<TabPanel value={innerTabValue} index={0}>
						<Paper>
							<Box p={4}>
								{currentEventSubmission?.submissions?.map(
									(submission, index) => (
										<Box mb={2} key={index}>
											<Grid container key={submission.id} spacing={2}>
												<Grid item xs={12} md={12} sx={{ marginBottom: 5 }}>
													<Typography
														sx={{ textAlign: 'center' }}
														variant="subtitle1"
													>
														{submission.video_title} {submission.id}
													</Typography>
												</Grid>
											</Grid>
											<Grid container key={submission.id} spacing={2}>
												<Grid item xs={12} md={6} sx={{ maxHeight: '500px' }}>
													<center>
														<Typography variant="caption">
															Benchmark Video
														</Typography>
													</center>
													<video
														width="100%"
														height={'90%'}
														controls
														src={submission?.event_video_url}
													/>
												</Grid>
												<Grid item xs={12} md={6} sx={{ maxHeight: '500px' }}>
													<center>
														<Typography variant="caption">
															Users Uploaded Attempt
														</Typography>
													</center>
													<video
														width="100%"
														height={'90%'}
														controls
														src={submission?.video_url}
													/>
												</Grid>
											</Grid>

											<Grid container key={submission.id} spacing={2}>
												<Grid item xs={12} md={12} sx={{ marginBottom: 5 }}>
													<TextInput
														multiline
														rows={4}
														name={submission.id}
														onChange={handleFeedbackChange}
														label="Feedback"
														placeholder="Feedback"
														fullWidth
														value={
															feedbackForm?.comments[submission?.id]?.comment ||
															''
														}
													/>
												</Grid>
											</Grid>
										</Box>
									)
								)}

								<Grid container spacing={2}>
									<Grid item xs={12} md={12}>
										<Typography
											variant="h5"
											sx={{ marginBottom: 2, textAlign: 'center' }}
										>
											User Playercard
										</Typography>
									</Grid>
									{PLAYER_CARD.map((skill) => (
										<Grid item xs={12} md={4} key={skill}>
											<Paper>
												<Box p={2}>
													<Typography variant="subtitle1">
														{capitalize(skill)}:
													</Typography>
													<Typography variant="subtitle1">
														{currentEventSubmission?.user?.player_card[skill] ??
															'-'}
													</Typography>
												</Box>
											</Paper>
										</Grid>
									))}
								</Grid>
								<Grid container spacing={2}>
									<Grid item xs={12} md={12} sx={{ marginTop: 8 }}>
										<Typography
											variant="h5"
											sx={{ marginBottom: 2, textAlign: 'center' }}
										>
											User Playercard Feedback
										</Typography>
									</Grid>
									{PLAYER_CARD.map((skill) => (
										<Grid item xs={12} md={4} key={skill}>
											<TextInput
												label={`${capitalize(skill)}`}
												name={skill}
												placeholder={`${capitalize(skill)}`}
												type={'number'}
												fullWidth
												handleChange={handleSkillChange}
												value={
													feedbackForm?.skills ? feedbackForm.skills[skill] : ''
												}
											/>
										</Grid>
									))}
								</Grid>
								<Grid container spacing={2}>
									<Grid
										item
										xs={12}
										md={3}
										sx={{ marginTop: 8, paddingBottom: 15 }}
									>
										<Button
											onClick={handleSubmitFeedback}
											variant="contained"
											color="primary"
											fullWidth
										>
											Save Feedback
										</Button>
									</Grid>
								</Grid>
							</Box>
						</Paper>
					</TabPanel>
					<TabPanel value={innerTabValue} index={1}>
						<Paper sx={{ padding: 5 }}>
							<UserPlayer
								user={currentEventSubmission && currentEventSubmission?.user}
							/>
						</Paper>
					</TabPanel>
				</Container>
			)}
		</div>
	);
};

export default SubmissionShow;
